import { type ReactNode, useCallback } from 'react';

import { Icon as GBIcon, type IconProps } from '@gitbook/icons';
import { type IconDefinition, icons } from '@gitbook/icons/icons';

import {
    GenericPicker,
    type GenericPickerProps,
    type GenericPickerRenderInfo,
} from './GenericPicker';

export interface IconPickerProps extends Pick<
    GenericPickerProps<IconDefinition>,
    'header' | 'ignoreKeys' | 'placeholder'
> {
    /**
     * Callback when an icon is selected.
     */
    onPick: (icon: string) => void;

    /**
     * Optional query to filter the icons.
     * If a query is passed, a callback onChangeQuery should be passed to update the query.
     */
    query?: string;

    /**
     * Callback when the query changes.
     */
    onChangeQuery?: (query: string) => void;

    /**
     * Optional color to apply to the icons.
     */
    color?: string;

    /**
     * Optional custom renderer for icons.
     */
    renderIcon?: (props: IconProps) => ReactNode;
}

function getKey(icon: IconDefinition) {
    return icon.icon;
}

const textKeys = ['label', 'search'];

/**
 * Component to pick an icon from a list of icons.
 */
export function IconPicker(props: IconPickerProps) {
    const renderIcon = props.renderIcon ?? ((iconProps: IconProps) => <GBIcon {...iconProps} />);
    const renderItem = useCallback(
        (icon: IconDefinition): GenericPickerRenderInfo => {
            return {
                label: icon.label,
                icon: renderIcon({ icon: icon.icon, style: { color: props.color } }),
            };
        },
        [props.color, renderIcon]
    );
    return (
        <GenericPicker<IconDefinition>
            listId="icons"
            title="Icons"
            items={icons}
            query={props.query}
            onChangeQuery={props.onChangeQuery}
            getKey={getKey}
            onPick={(icon) => props.onPick(icon.icon)}
            renderItem={renderItem}
            textKeys={textKeys}
            header={props.header}
            iconSize={20}
            ignoreKeys={props.ignoreKeys}
            placeholder={props.placeholder}
        />
    );
}
